<script setup lang="ts">
import { Spinner } from "@/components/ui/spinner";
import { ShieldCheckIcon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import WordmarkLogo from "./WordmarkLogo.vue";
import { isIOS } from "../capacitor/platform";
import { useTheme } from "../queries/theme";
import {
  vpnPermissionPromptVisible,
  retryVpnPermission,
  dismissVpnPermissionPrompt,
} from "../capacitor/vpn-permission";

const { t } = useI18n();

const portalTheme = useTheme();
const theme = computed(() => portalTheme.accent);
const highContrast = computed(() => portalTheme.highAccessibility);

const accentVar = computed(() => `--theme-${theme.value ?? "grape"}${highContrast.value ? "-hc" : ""}`);
const iconContainerStyle = computed(() => ({
  backgroundColor: `hsl(var(${accentVar.value}) / 0.12)`,
  borderColor: `hsl(var(${accentVar.value}) / 0.4)`,
}));
const iconStyle = computed(() => ({
  color: `hsl(var(${accentVar.value}))`,
}));
const allowButtonClass = computed(() => `theme-${theme.value ?? "grape"}${highContrast.value ? "-hc" : ""}`);

const busy = ref(false);

const visible = computed(() => isIOS() && vpnPermissionPromptVisible.value);

async function handleAllow() {
  if (busy.value) return;
  busy.value = true;
  try {
    await retryVpnPermission();
  } finally {
    busy.value = false;
  }
}

function handleNotNow() {
  if (busy.value) return;
  dismissVpnPermissionPrompt();
}
</script>

<template>
  <Teleport to="body">
    <Transition name="vpn-perm-fade">
      <div
        v-if="visible"
        class="fixed inset-0 z-95 flex min-h-full flex-1 flex-col justify-center bg-background pt-24 sm:px-6 lg:px-8"
      >
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
          <WordmarkLogo class="mx-auto dark:opacity-50" />
        </div>

        <div class="mt-10 flex flex-col items-center gap-6 sm:mx-auto sm:w-full sm:max-w-sm">
          <div class="flex flex-col items-center gap-4 px-4">
            <div :style="iconContainerStyle" class="flex h-14 w-14 items-center justify-center rounded-full border">
              <ShieldCheckIcon :style="iconStyle" class="h-7 w-7" />
            </div>

            <h1 class="text-center text-xl font-semibold text-foreground">
              {{ t("msg.vpnPermRequired") }}
            </h1>

            <p class="text-center text-sm text-muted-foreground">
              {{ t("msg.vpnPermRequiredDescription") }}
            </p>
          </div>

          <div class="w-full space-y-3 px-4">
            <button
              @click="handleAllow"
              :disabled="busy"
              :class="allowButtonClass"
              class="flex w-full items-center justify-center rounded-md px-3 py-2.5 text-sm font-semibold text-primary-foreground shadow-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Spinner v-if="busy" class="mr-2" />
              {{ t("msg.vpnPermAllow") }}
            </button>
            <button
              @click="handleNotNow"
              :disabled="busy"
              class="flex w-full items-center justify-center rounded-md border border-border bg-background px-3 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              {{ t("msg.vpnPermNotNow") }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.vpn-perm-fade-enter-active,
.vpn-perm-fade-leave-active {
  transition: opacity 0.3s ease;
}
.vpn-perm-fade-enter-from,
.vpn-perm-fade-leave-to {
  opacity: 0;
}
</style>
